import { Listbox } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

import getDistinctRequest from '../../../lib/api/getDistinctRequest';
import { useRegion } from '../../../lib/providers/regionProvider';
import orderBy from 'lodash/orderBy';
import useScrollPosition from '../../../lib/hooks/useScrollPosition';
import { throttle, uniq } from 'lodash';
import JobLevelOptions from '../JobLevelOptoins';
import RiasecFilterOptions from '../RiasecFilterOptions';
import FilterCheckbox from './FilterCheckbox';
import FilterRadio from './FilterRadio';
import elIsInView from '../../../lib/fns/elIsInView';
import Spinner from '../../icons/Spinner';

interface StandardFilterOptionsProps {
  filterOptions: any[];
  paramLabel: string;
  singleSelect?: boolean;
}

const dynamicParamLabels = ['jobTitle', 'employer', 'industry', 'careers']


const StandardFilterOptions: React.FC<StandardFilterOptionsProps> = ({
  filterOptions,
  paramLabel,
  singleSelect
}) => {
  const {
    query
  } = useRouter();
  const { region } = useRegion();
  const [page, setPage] = useState(1);

  const [fetchedOptions, setFetchedOptions] = useState([] as string[]);
  const { userLocationInfo } = useRegion();

  const { data, isLoading: optionsLoading } = useQuery(
    ['filter-options', paramLabel, location, region, page, userLocationInfo],
    () => {
      if (paramLabel == 'jobTitle') {
        return getDistinctRequest('jobs', 'title_name', { ...query, page, coordinates: userLocationInfo?.location?.coordinates });
      }

      if (paramLabel == 'employer') {
        return getDistinctRequest('jobs', 'company_name', { ...query, page, coordinates: userLocationInfo?.location?.coordinates });
      }

      if (paramLabel == 'industry') {
        return getDistinctRequest('jobs', 'career_area_name', { ...query, page, coordinates: userLocationInfo?.location?.coordinates });
      }

      if (paramLabel == 'careers') {
        return getDistinctRequest(
          'programs',
          'primaryCareerData__career__careerName',
          { region: region?.slug, page },
        );
      }
      return [];
    },
  );
  const isComplete = useMemo(() => data && !data?.length || data?.length % 20 > 0 ? true : false, [data])

  const options = useMemo(() => {
    return paramLabel == 'distance' ? filterOptions : filterOptions?.length ? orderBy(filterOptions) : orderBy(fetchedOptions);
  }, [fetchedOptions]);

  const infiniteScroll = useRef<HTMLDivElement>(null)

  const { scrollPosition } = useScrollPosition();
  useEffect(() => {
    loadMoreItemsThrottled();
  }, [scrollPosition])

  // check if the infinite scroll component is in view
  const checkInView = () => {
    if (infiniteScroll.current) {
      return elIsInView(infiniteScroll.current)
    }
    return false;
  }

  // Nav to next page of filter results
  const loadMoreItems = () => {
    if (checkInView() && !isComplete) {
      setPage(page + 1)
    }
  }

  // Throttle the load more items
  const loadMoreItemsThrottled = throttle(loadMoreItems, 2000)
  useEffect(() => {
    if (data) {
      // I think i did this as a hack, hopefully we can get rid of uniq
      setFetchedOptions(uniq([...fetchedOptions, ...data]))
    }
  }, [data])


  // We always want to reset the page to 1 when a query parameter changes
  useEffect(() => {
    setPage(1);
  }, [query])


  return (
    <>
      {Boolean(!filterOptions.length && optionsLoading) && (
        <div className="flex items-center justify-center p-4">
          <Spinner className="w-5 h-5 color-primary" />
        </div>
      )}
      {options?.map((filter: any, index: number) => (
        <Listbox.Option key={filter} value={filter}>
          {({ selected }) => (
            <div className={`py-1 pr-6 relative flex items-center`}>
              {Boolean(!singleSelect) ? <FilterCheckbox selected={selected} paramLabel={paramLabel} index={index} /> : <FilterRadio selected={selected} paramLabel={paramLabel} index={index}></FilterRadio>}

              {paramLabel == 'style' || paramLabel == 'workStyle' ? (
                <RiasecFilterOptions filter={filter} />
              ) : paramLabel == 'jobLevel' ?

                <JobLevelOptions filter={filter} /> :
                (
                  <label data-testid={`option_${index}`} className="text-lg capitalize cursor-pointer text-gray1">
                    {filter}
                  </label>
                )}
            </div>
          )}
        </Listbox.Option>
      ))}
      {
        Boolean(!isComplete && !optionsLoading && dynamicParamLabels.includes(paramLabel)) &&
        <div ref={infiniteScroll} className="flex items-center justify-center p-4">
          <Spinner className="w-5 h-5 color-primary" />
        </div>
      }
    </>
  );
};

export default StandardFilterOptions;
